/**
 * AI 어시스턴트 버그·오류 신고
 *
 * 흐름:
 *   패널에서 설명 입력 → erp-chat(action:bug_report) → Apps Script → 시트 "버그신고"
 *   개발자가 시트 "답변" 열에 적음 → 패널이 bug_status 로 읽어 카드로 표시
 *
 * 시크릿(SHEET_LOG_*) 은 브라우저에 두지 않는다. Gemini 중계와 같은 Edge Function·앱 토큰을 쓴다.
 */

import { getUserInfo } from '../../utils/tokenManager';
import { getAuthInfo } from '../authService';
import { isDemoMode } from '../../demo/demoMode';
import { isAiConfigured } from './geminiClient';

const FUNCTION_NAME = 'erp-chat';
const STORAGE_KEY = 'erp-ai-bug-tickets';
const MAX_TICKETS = 30;
const MAX_MSG = 8;
const MAX_MSG_CHARS = 220;

const getSupabaseUrl = () => (process.env.REACT_APP_SUPABASE_URL || '').trim().replace(/\/$/, '');
const getAnonKey = () => (process.env.REACT_APP_SUPABASE_ANON_KEY || '').trim();
const getAppToken = () => (process.env.REACT_APP_ERP_APP_TOKEN || '').trim();

/** 전화·주민번호처럼 흔한 패턴만 가린다. 완전 마스킹은 아니다. */
export function maskSensitive(text) {
  return String(text || '')
    .replace(/\b01[016789]-?\d{3,4}-?\d{4}\b/g, '[전화]')
    .replace(/\b\d{6}-\d{7}\b/g, '[주민]');
}

/** 채팅 메시지를 시트에 넣을 짧은 스냅샷으로 만든다. */
export function buildConversationSnapshot(messages = []) {
  const recent = (Array.isArray(messages) ? messages : []).slice(-MAX_MSG);
  return recent
    .map((m) => {
      const role = m.role === 'user' ? '사용자' : m.role === 'tool' ? '도구' : m.role === 'dev-reply' ? '개발자' : '도우미';
      const body = maskSensitive(String(m.text || '').replace(/\s+/g, ' ').trim()).slice(0, MAX_MSG_CHARS);
      return `${role}: ${body}`;
    })
    .filter(Boolean)
    .join('\n');
}

export function buildDebugPayload({ route, screenLabel, lastError } = {}) {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  return {
    href: typeof window !== 'undefined' ? String(window.location.href || '').slice(0, 400) : '',
    route: route || '',
    screenLabel: screenLabel || '',
    userAgent: String(ua).slice(0, 300),
    language: typeof navigator !== 'undefined' ? navigator.language || '' : '',
    viewport:
      typeof window !== 'undefined'
        ? `${window.innerWidth || 0}x${window.innerHeight || 0}`
        : '',
    demo: isDemoMode(),
    lastError: maskSensitive(String(lastError || '')).slice(0, 800),
    at: new Date().toISOString(),
  };
}

function loadTickets() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

function saveTickets(list) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(0, MAX_TICKETS)));
  } catch {
    /* 용량·사생활 모드 — 무시 */
  }
}

export function listBugTickets() {
  return loadTickets();
}

export function upsertBugTicket(ticket) {
  const list = loadTickets().filter((t) => t.id !== ticket.id);
  list.unshift(ticket);
  saveTickets(list);
  return ticket;
}

export function updateBugTicket(id, patch) {
  const list = loadTickets().map((t) => (t.id === id ? { ...t, ...patch } : t));
  saveTickets(list);
  return list.find((t) => t.id === id) || null;
}

async function callErpChat(body) {
  if (!isAiConfigured()) {
    throw new Error('AI 연결 설정이 없어 신고를 보낼 수 없습니다.');
  }

  let response;
  try {
    response = await fetch(`${getSupabaseUrl()}/functions/v1/${FUNCTION_NAME}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: getAnonKey(),
        Authorization: `Bearer ${getAnonKey()}`,
        'x-erp-app-token': getAppToken(),
      },
      body: JSON.stringify(body),
    });
  } catch (error) {
    throw new Error(`신고 서버에 연결하지 못했습니다: ${error?.message || error}`);
  }

  const raw = await response.text();
  let json = null;
  try {
    json = raw ? JSON.parse(raw) : null;
  } catch {
    /* below */
  }

  if (!response.ok || json?.ok === false) {
    throw new Error(json?.error || raw?.slice(0, 300) || `HTTP ${response.status}`);
  }
  return json;
}

/**
 * 버그 신고를 접수한다.
 * @returns {{ ticketId: string }}
 */
export async function submitBugReport({
  description,
  route,
  screenLabel,
  messages,
  lastError,
} = {}) {
  const trimmed = String(description || '').trim();
  if (!trimmed) throw new Error('무엇을 신고할지 적어 주세요.');

  const { companyNo, userNo } = getUserInfo() || {};
  const { userName } = getAuthInfo() || {};
  const debug = buildDebugPayload({ route, screenLabel, lastError });

  const result = await callErpChat({
    action: 'bug_report',
    description: maskSensitive(trimmed).slice(0, 1500),
    route: route || '',
    href: debug.href,
    user_agent: debug.userAgent,
    conversation: buildConversationSnapshot(messages),
    error_summary: maskSensitive(String(lastError || '')).slice(0, 1000),
    debug: JSON.stringify(debug),
    demo: isDemoMode(),
    user: {
      name: userName || '',
      userNo: userNo || '',
      companyNo: companyNo || '',
    },
  });

  const ticketId = result.ticket_id;
  upsertBugTicket({
    id: ticketId,
    at: new Date().toISOString(),
    route: route || '',
    screenLabel: screenLabel || '',
    description: trimmed.slice(0, 300),
    reply: '',
    status: '접수',
    seen: false,
  });

  return { ticketId };
}

/** 아직 답변이 없는 티켓들의 상태를 조회하고, 답변이 있으면 localStorage 에 반영한다. */
export async function pollBugReplies() {
  const tickets = loadTickets();
  const pending = tickets.filter((t) => t.id && !t.reply);
  if (!pending.length) return [];

  const updates = [];
  // 동시에 너무 많이 치면 Apps Script 가 바쁘다. 최대 5개만.
  for (const ticket of pending.slice(0, 5)) {
    try {
      const result = await callErpChat({ action: 'bug_status', ticket_id: ticket.id });
      const reply = String(result.reply || '').trim();
      if (reply) {
        const next = updateBugTicket(ticket.id, {
          reply,
          status: result.status || '답변완료',
          repliedAt: new Date().toISOString(),
        });
        if (next) updates.push(next);
      } else if (result.status) {
        updateBugTicket(ticket.id, { status: result.status });
      }
    } catch {
      /* 폴링 실패는 조용히 — 다음에 다시 */
    }
  }
  return updates;
}
